import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import http, { communityApi } from '../services/api';
import { Award, Sparkles, ChevronRight, Star, Users, ShoppingBag, Ticket, Gavel, HeartHandshake, Share2, Copy, DollarSign } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const CATEGORY_ICONS = {
  engagement: Star,
  events: Ticket,
  purchases: ShoppingBag,
  bidding: Gavel,
  donations: HeartHandshake,
  referrals: Users,
};
const CATEGORY_LABELS = {
  engagement: 'Engagement',
  events: 'Events',
  purchases: 'Purchases',
  bidding: 'Bidding',
  donations: 'Donations',
  referrals: 'Referrals',
};

/** Platform Loyalty Card — 10 stamps → +100 pts bonus. */
export default function PBBLoyaltyCard() {
  const [s, setS] = useState(null);
  const [loaded, setLoaded] = useState(false);
  const [reserve, setReserve] = useState(null);

  useEffect(() => {
    http.get('/platform-loyalty/me').then(r => setS(r.data)).catch(() => {}).finally(() => setLoaded(true));
    // Pull my current cash-reserve total from the same referral endpoint so
    // one link, one code drives BOTH the points bonus and the 3% reserve.
    communityApi.myReferral().then(setReserve).catch(() => {});
  }, []);

  const stamps = s?.stamps || 0;
  const total = s?.target || 10;
  const pct = Math.min(100, (stamps / total) * 100);
  const remaining = Math.max(0, total - stamps);
  const refCode = s?.referralCode || '';
  const refUrl = refCode ? `${window.location.origin}/signup?ref=${refCode}` : '';

  const copyRef = async () => {
    try { await navigator.clipboard.writeText(refUrl); toast({ title: 'Referral link copied!', description: refUrl }); } catch {}
  };

  return (
    <div className="pbb-card p-5" data-testid="pbb-loyalty-card">
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2 font-semibold">
          <Award size={16} className="text-green-400"/>
          Platform Loyalty Card
        </div>
        <span className="text-[10px] uppercase tracking-wider text-green-400 bg-green-600/10 border border-green-600/30 px-2 py-0.5 rounded-full inline-flex items-center gap-1">
          <Sparkles size={10}/> Auto-earn
        </span>
      </div>

      <div className="flex items-baseline gap-2">
        <div className="text-3xl font-bold text-green-400" data-testid="pbb-loyalty-progress">{loaded ? stamps : '—'}</div>
        <div className="text-sm text-gray-400">/ {total} stamps</div>
        {(s?.completions || 0) > 0 && (
          <div className="ml-auto text-[11px] text-gray-400">Completions: <b className="text-white">{s.completions}</b> · <b className="text-green-400">+{s.bonusEarned}</b> pts</div>
        )}
      </div>
      <div className="h-2 mt-2 rounded-full bg-[#111] overflow-hidden">
        <div className="h-full bg-green-500 transition-all" style={{ width: `${pct}%` }} />
      </div>
      <div className="grid grid-cols-5 sm:grid-cols-10 gap-1.5 mt-3">
        {Array.from({ length: total }).map((_, i) => (
          <div key={i} className={`aspect-square rounded-md border ${i < stamps ? 'bg-green-500/25 border-green-400 text-green-300 shadow-[0_0_0_1px_rgba(74,222,128,0.35)]' : 'bg-[#1a2a20] border-dashed border-green-600/40 text-gray-500'} flex items-center justify-center text-xs font-bold`}>
            {i < stamps ? '★' : '·'}
          </div>
        ))}
      </div>
      <div className="text-xs text-gray-400 mt-2">
        {remaining === 0
          ? <span className="text-green-400">Full card — bonus credited!</span>
          : <>{remaining} more stamp{remaining === 1 ? '' : 's'} to auto-earn <b className="text-green-400">+{s?.bonus || 100}</b> pts.</>}
      </div>

      {/* Category breakdown */}
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 gap-2 text-[11px]" data-testid="pbb-category-breakdown">
        {Object.entries(CATEGORY_LABELS).map(([key, label]) => {
          const Icon = CATEGORY_ICONS[key];
          const val = s?.byCategory?.[key] || 0;
          return (
            <div key={key} className="rounded-lg bg-[#0d1f14] border border-[#153b23] px-2 py-1.5 flex items-center gap-1.5">
              <Icon size={12} className="text-green-400 shrink-0"/>
              <span className="text-gray-400 truncate">{label}</span>
              <b className="text-green-400 ml-auto">{val}</b>
            </div>
          );
        })}
      </div>

      {/* Referral share — one link drives BOTH the +100 pt bonus and the 3%
          cash reserve toward the KC pilot. */}
      {refUrl && (
        <div className="mt-4 rounded-lg bg-[#0d1f14] border border-[#153b23] p-3 text-xs" data-testid="referral-block">
          <div className="flex items-center gap-2">
            <Share2 size={12} className="text-green-400"/>
            <span className="text-gray-300">Referral link</span>
            <b className="text-green-400 ml-auto">+{s.referralEarned || 0} pts earned</b>
          </div>
          <div className="mt-2 flex gap-2">
            <div className="flex-1 truncate bg-black/40 rounded px-2 py-1.5 text-gray-300">{refUrl}</div>
            <button onClick={copyRef} data-testid="copy-ref-link" className="px-2 py-1.5 rounded bg-green-600 hover:bg-green-700 text-white inline-flex items-center gap-1"><Copy size={12}/> Copy</button>
          </div>
          <div className="text-gray-500 mt-1">Earn +100 pts every time someone you invite fills their loyalty card.</div>
          <div className="mt-2 rounded bg-black/40 border border-green-500/20 p-2 flex items-center gap-2"
               data-testid="referral-reserve-block">
            <DollarSign size={12} className="text-green-400 shrink-0"/>
            <div className="flex-1">
              <div className="text-gray-300">Cash Reserved for Launch</div>
              <div className="text-[10px] text-gray-500">3% of every qualifying purchase from your invites · releases April 1, 2027</div>
            </div>
            <b className="text-green-400 text-sm tabular-nums" data-testid="referral-reserve-amount">
              ${(reserve?.totalReserved || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </b>
          </div>
        </div>
      )}

      <Link to="/loyalty" className="mt-3 inline-flex items-center gap-1 text-green-400 text-sm hover:underline">
        See details <ChevronRight size={14}/>
      </Link>
    </div>
  );
}
